import type { TripPosition } from '../api'

export const ROUTE_ANIMATION_DURATION_MS = 5_000
// The mobile apps normally upload a position about every ten seconds. Keeping
// the interpolation slightly longer lets the next real point arrive before the
// marker stops, while avoiding speculative movement beyond reported positions.
export const LIVE_ROUTE_ANIMATION_DURATION_MS = 10_500

const clockFormatter = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

function clampProgress(progress: number): number {
  return Math.min(1, Math.max(0, progress))
}

function timestamp(value: string): number {
  return Date.parse(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
}

function positionTimes(positions: TripPosition[]): number[] | null {
  const times = positions.map((position) => timestamp(position.recordedAt))
  const monotonic = times.every((time, index) =>
    Number.isFinite(time) && (index === 0 || time >= times[index - 1]!),
  )
  return monotonic && times.length > 1 && times.at(-1)! > times[0]! ? times : null
}

export interface TripTimeline {
  elapsedSecondsAt(progress: number): number
  positionIndexAt(progress: number): number
  positionSampleAt(progress: number): TripPositionSample
  progressAtTime(timestampValue: number): number | null
  progressAtPositionIndex(index: number): number
  timeAt(progress: number): number | null
}

export interface TripPositionSample {
  index: number
  nextIndex: number | null
  fraction: number
}

export function createTripTimeline(positions: TripPosition[]): TripTimeline {
  const times = positionTimes(positions)
  const firstTime = times?.[0]
  const duration = times ? times.at(-1)! - times[0]! : 0

  const positionIndexAt = (progress: number): number => {
    if (positions.length === 0) return -1
    const normalized = clampProgress(progress)
    if (normalized === 1) return positions.length - 1
    if (!times) return Math.floor(normalized * (positions.length - 1))

    const target = firstTime! + duration * normalized
    let low = 0
    let high = times.length - 1
    while (low < high) {
      const middle = Math.ceil((low + high) / 2)
      if (times[middle]! <= target) low = middle
      else high = middle - 1
    }
    return low
  }

  const progressAtPositionIndex = (index: number): number => {
    if (positions.length < 2) return 0
    const normalizedIndex = Math.min(positions.length - 1, Math.max(0, index))
    return times
      ? (times[normalizedIndex]! - firstTime!) / duration
      : normalizedIndex / (positions.length - 1)
  }

  return {
    elapsedSecondsAt: (progress) => times
      ? Math.round((duration * clampProgress(progress)) / 1_000)
      : 0,
    positionIndexAt,
    positionSampleAt: (progress) => {
      const normalized = clampProgress(progress)
      const index = positionIndexAt(normalized)
      if (index < 0 || index >= positions.length - 1) {
        return { index, nextIndex: null, fraction: 0 }
      }
      const from = progressAtPositionIndex(index)
      const to = progressAtPositionIndex(index + 1)
      const fraction = to > from
        ? Math.min(1, Math.max(0, (normalized - from) / (to - from)))
        : 0
      return { index, nextIndex: index + 1, fraction }
    },
    progressAtTime: (timestampValue) => times && Number.isFinite(timestampValue)
      ? clampProgress((timestampValue - firstTime!) / duration)
      : null,
    progressAtPositionIndex,
    timeAt: (progress) => {
      if (times) return firstTime! + duration * clampProgress(progress)
      const index = positionIndexAt(progress)
      return index < 0 ? null : timestamp(positions[index]!.recordedAt)
    },
  }
}

export function positionIndexAtProgress(
  positions: TripPosition[],
  progress: number,
): number {
  return createTripTimeline(positions).positionIndexAt(progress)
}

export function progressAtPositionIndex(
  positions: TripPosition[],
  index: number,
): number {
  return createTripTimeline(positions).progressAtPositionIndex(index)
}

export function initialLiveProgress(
  positions: TripPosition[],
  visiblePositionCount = 4,
): number {
  const startIndex = Math.max(0, positions.length - Math.max(1, visiblePositionCount))
  return createTripTimeline(positions).progressAtPositionIndex(startIndex)
}

export function timeAtProgress(
  positions: TripPosition[],
  progress: number,
): number | null {
  return createTripTimeline(positions).timeAt(progress)
}

export function elapsedSecondsAtProgress(
  positions: TripPosition[],
  progress: number,
): number {
  return createTripTimeline(positions).elapsedSecondsAt(progress)
}

export function formatClock(timestampValue: number | null): string {
  if (timestampValue === null || !Number.isFinite(timestampValue)) return '–'
  return clockFormatter.format(new Date(timestampValue))
}

export function formatElapsed(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3_600)
  const minutes = Math.floor((totalSeconds % 3_600) / 60)
  const seconds = totalSeconds % 60
  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, '0'))
    .join(':')
}
